import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import BranchForm, {
  BranchFormRef,
  BranchFormValues,
} from '../components/BranchForm';
import { useUpdateAgencyBranchById } from '@nxsys/hooks/api/agency';
import { CreateAgencyBranchParams } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import AppDescription from '@nxsys/components/AppDescription';
import { Descriptions, Tag } from 'antd';
import { countryList, getVATCodeOption } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';

type DetailBranchModalProps = {
  isOpen: boolean;
  toggle: () => void;
  agencyData: IAgency;
  refetchData: () => void;
  data: IBranch | null;
};

const DetailBranchModal: React.FC<DetailBranchModalProps> = ({
  isOpen,
  toggle,
  agencyData,
  refetchData,
  data: dataProps,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const agencyId = agencyData.id;

  const [isEditing, setIsEditing] = useState(true);

  const {
    run: updateAgencyBranchById,
    loading: isUpdatingAgencyBranch,
    value: updateAgencyBranchByIdValue,
  } = useUpdateAgencyBranchById();
  const updatedData = updateAgencyBranchByIdValue?.data;
  const data = updatedData ?? dataProps;

  const formRef = useRef<BranchFormRef>(null);
  const handleSubmit = async (values: BranchFormValues) => {
    if (!agencyId || !data?.id) return;
    const params: CreateAgencyBranchParams = {
      siteName: values.siteName,
      code: values.code,
      VATCode: values.VATCode?.value ?? null,
      siteManager: values.siteManager?.value ?? null,
      isSameAsAgency: values.isSameAsAgency,
    };
    if (values.isSameAsAgency) {
      params.address = (agencyData.address || []).map((v) => v ?? '');
      params.city = agencyData.city;
      params.county = agencyData.county;
      params.postCode = agencyData.postCode;
      if (agencyData.country) params.country = agencyData.country;
    } else {
      params.companyName = values.companyName;
      params.address = [values.address1 ?? '', values.address2 ?? '', values.address3 ?? ''];
      params.city = values.city;
      params.county =
        typeof values.county === 'object'
          ? (values.county as any)?.value ?? ''
          : values.county ?? '';
      params.postCode = values.postCode;
      params.country = values.country?.value ?? '';
    }
    updateAgencyBranchById(data.id, params).then(() => {
      setIsEditing(false);
      refetchData();
      showMessage({
        type: 'success',
        content: messages['common.updateAgencyBranchSuccess'],
      });
    });
  };

  return (
    <AppModalContainer
      title={messages['common.siteDetail']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        !isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: toggle,
              },
              {
                name: messages['common.edit'],
                onClick: () => setIsEditing(true),
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
              },
            ]
          : [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isUpdatingAgencyBranch,
                disabled: isUpdatingAgencyBranch,
              },
            ]
      }
      width={800}
    >
      {!isEditing ? (
        <AppDescription
          column={1}
          labelStyle={{ width: 300 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          <Descriptions.Item label={messages['common.siteName']}>
            {data?.siteName ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.code']}>
            {data?.code ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.vatCode']}>
            {data?.VATCode?.VATCode ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.siteManager']}>
            {data?.siteManager?.name ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={messages['form.isSameInvoicingDetailAsAgencyDetails']}
          >
            <Tag>
              {data?.isSameAsAgency
                ? messages['common.yes']
                : messages['common.no']}
            </Tag>
          </Descriptions.Item>
          {!data?.isSameAsAgency && (
            <>
              <Descriptions.Item label={messages['common.companyName']}>
                {data?.companyName ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.address1']}>
                {data?.address?.[0] ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.address2']}>
                {data?.address?.[1] ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.address3']}>
                {data?.address?.[2] ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.cityTown']}>
                {data?.city ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.county']}>
                {data?.county ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages[isIreland ? 'common.eirCode' : 'common.postCode']}>
                {data?.postCode ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.country']}>
                {data?.country ?? '-'}
              </Descriptions.Item>
            </>
          )}
        </AppDescription>
      ) : (
        <BranchForm
          formRef={formRef}
          onSubmit={handleSubmit}
          agencyId={agencyId}
          defaultValues={{
            siteName: data?.siteName ?? '',
            code: data?.code ?? '',
            VATCode: data?.VATCode ? getVATCodeOption(data?.VATCode) : null,
            siteManager: data?.siteManager
              ? {
                  label: data?.siteManager?.name ?? '',
                  value: data?.siteManager?.id ?? '',
                }
              : null,
            isSameAsAgency: data?.isSameAsAgency ?? true,
            companyName: data?.companyName ?? '',
            address1: data?.address?.[0] ?? '',
            address2: data?.address?.[1] ?? '',
            address3: data?.address?.[2] ?? '',
            city: data?.city ?? '',
            county: data?.county ?? '',
            postCode: data?.postCode ?? '',
            country: data?.country
              ? {
                  label:
                    countryList.find((c) => c.value === data?.country)?.label ??
                    '',
                  value: data?.country ?? '',
                }
              : null,
          }}
        />
      )}
    </AppModalContainer>
  );
};

export default DetailBranchModal;
